import {type Dispatch, type SetStateAction} from "react";
import type {SourceState} from "../Sources.tsx";
import {type AudioPlayer} from "./AudioPlayer";
import Ramp from "./ramp/Ramp";
import {isSafari} from "./SafariPlayerHelper";

console.log("isSafari: " + isSafari());


export default class StreamPlayer implements AudioPlayer {

    private readonly url: string;
    private readonly setState: Dispatch<SetStateAction<SourceState>>
    private context: AudioContext;
    private readonly panner?: PannerNode;
    private readonly stereoPanner?: StereoPannerNode;
    private source?: MediaElementAudioSourceNode;
    private audioEl?: HTMLAudioElement;

    private playing = false;
    private volumeSetting = 0;
    private readonly ramp: Ramp;

    constructor(context: AudioContext, url: string, setState: React.Dispatch<React.SetStateAction<SourceState>>) {
        this.url = url;
        this.setState = setState;
        this.context = context;

        this.panner = this.context.createPanner();
        this.stereoPanner = this.context.createStereoPanner(); // not working for !iOS-Safari

        // ramp is acting on audio element volume
        this.ramp = new Ramp((value) => {
            //console.debug("ramp value to " + value.toFixed(3));
            if (this.audioEl) this.audioEl.volume = value;
        });
    }

    load(loaded: (success: boolean) => void) {
        this.setState("loading");
        try {
            this.audioEl = new Audio(this.url);
            this.audioEl.crossOrigin = "anonymous";

            this.audioEl.addEventListener("error", (error) => {
                console.error("Audio element error: ", error);
                this.setState("error");
            });

            this.audioEl.addEventListener("ended", () => {
                this.stop();
                this.setState("ready");
            });

            this.source = this.context.createMediaElementSource(this.audioEl);
            if (isSafari()) {
                this.source.connect(this.panner!).connect(this.context.destination);
            } else {
                this.source.connect(this.stereoPanner!).connect(this.context.destination);
            }

            this.setState("ready");
            loaded(true);
        } catch (error) {
            console.error("error loading audio: ", error);
            this.setState("error");
            loaded(false);
        }
    }

    loop() {
        if (!this.audioEl) return;

        this.audioEl.loop = true;
    }

    toggle() {
        //console.trace("stream player - toggle playback");
        if (!this.playing) {
            const ctxState = this.context.state;
            if (ctxState === "running") {
                this.start();
            } else {
                this.context.resume().then(() => {
                    console.log("audio context was " + ctxState + ", now " + this.context.state);
                    this.start();
                }).catch((error) => {
                    console.error("Audio context resume error: ", error);
                    this.setState("error");
                });
            }
        } else {
            this.stop();
        }
    }

    volume = (dB: number) => {
        //console.debug("stream: attenuate by " + dB.toFixed(1) + " dB.");
        const value = Math.pow(10, (dB / 20));

        // change the volume of audio only during playback and not triggered by pure playback state changes
        if (this.playing && this.volumeSetting !== value) {
            if (this.audioEl) this.audioEl.volume = value;
        }

        // this is what the user sees
        this.volumeSetting = value;
    }

    // vertical panning is not supported by StereoPannerNode
    pan = (horizontal: number) => {
        if (this.stereoPanner) this.stereoPanner.pan.setValueAtTime(horizontal, this.context.currentTime);
        if (this.panner) this.panner.positionX.value = horizontal;
    }

    private start() {
        if (!this.audioEl) return;

        this.audioEl.play().then(() => {
            this.ramp.on(this.volumeSetting).then(() => {
                // ramp complete
            });
            this.playing = true;
            this.setState("running");
        }).catch((error) => {
            console.error("Audio element play error: ", error);
            this.setState("error");
        });
    }

    private stop() {

        this.ramp.off(this.volumeSetting).then(() => {
            if (!this.audioEl) return;

            this.audioEl.pause();
            this.playing = false;
            this.setState("ready");
        });
    }
}